import {
  customerPathFromSignature,
  isDefined,
  isLinkState,
} from "@timeedit/registration-shared";
import { useCallback } from "react";
import {
  NavigateOptions,
  To,
  useLocation,
  useNavigate,
} from "react-router-dom";

export function useCustomerNavigation(
  customerSignature: string | null | undefined,
  root: string
): (to: To, options?: NavigateOptions) => void {
  const location = useLocation();
  const navigate = useNavigate();

  return useCallback(
    (to: To, options?: NavigateOptions) => {
      // Auto-forward state.from so the back-arrow chain survives sibling/child
      // route swaps. Caller-provided state.from overrides.
      const callerState = options?.state;

      const callerIsLinkState = isLinkState(callerState);
      const callerHasFrom = callerIsLinkState && isDefined(callerState.from);
      const locationHasFrom =
        isLinkState(location.state) && isDefined(location.state.from);

      let finalOptions: NavigateOptions | undefined = options;
      if (!callerHasFrom && locationHasFrom) {
        const linkState = callerIsLinkState
          ? { from: location.state.from, ...callerState }
          : { from: location.state.from };
        finalOptions = {
          ...options,
          state: linkState,
        };
      }

      if (!customerSignature) {
        return navigate(to, finalOptions);
      }
      const customerPath = customerPathFromSignature(customerSignature);
      const customerPathPrefix = `/${customerPath}`;
      const rootPath = `${customerPathPrefix}/${root}`;

      const getPathname = (to: string) => {
        return to.startsWith(customerPathPrefix)
          ? to
          : `${customerPathPrefix}${to}`;
      };

      if (typeof to === "string") {
        if (!to.startsWith("/")) {
          return navigate(to, finalOptions);
        }

        const pathname = getPathname(to);
        return navigate({ pathname, search: location.search }, finalOptions);
      } else {
        if (!to.pathname) {
          return navigate(rootPath);
        } else {
          if (!to.pathname.startsWith("/")) {
            return navigate(to, finalOptions);
          }
          const pathname = getPathname(to.pathname);
          return navigate({ ...to, pathname }, finalOptions);
        }
      }
    },
    [customerSignature, location.search, location.state, navigate, root]
  );
}
